import { useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { inr } from "./CostSummary";

const STATUS_STYLE = {
  added: { bg: "rgba(5,150,105,0.12)", color: "var(--c-green)", label: "🟢 Added" },
  removed: { bg: "rgba(220,38,38,0.12)", color: "var(--c-red)", label: "🔴 Removed" },
  changed: { bg: "rgba(217,119,6,0.12)", color: "var(--c-amber)", label: "🟡 Changed" },
  same: { bg: "transparent", color: "var(--c-muted)", label: "— Same" },
};

export default function BomCompare({ projectId, versions, liveEdits }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [bLive, setBLive] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!a || (!b && !bLive)) return;
    setBusy(true);
    try {
      const { data: d } = await api.get(`/bom/${projectId}/compare`, { params: { a: Number(a), b: bLive ? 0 : Number(b), live: bLive } });
      setData(d);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const fmtCell = (it) => !it ? "—" : `${it.quantity ?? ""} ${it.unit ?? ""}${it.total_cost != null ? ` · ${inr(it.total_cost)}` : ""}`;
  const pct = (x, y) => (x && y) ? `${y > x ? "+" : ""}${Math.round(1000 * (y - x) / x) / 10}%` : "—";

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3 space-y-3" data-testid="bom-compare">
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="compare-a" value={a} onChange={(e) => setA(e.target.value)}
          className="h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2">
          <option value="">Version A…</option>
          {versions.map((v) => <option key={v.version} value={v.version}>v{v.version} — {v.label}</option>)}
        </select>
        <span className="text-[var(--c-muted)] text-[11px]">→</span>
        <select data-testid="compare-b" value={bLive ? "live" : b}
          onChange={(e) => e.target.value === "live" ? (setBLive(true), setB("")) : (setBLive(false), setB(e.target.value))}
          className="h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2">
          <option value="">Version B…</option>
          {versions.map((v) => <option key={v.version} value={v.version}>v{v.version} — {v.label}</option>)}
          <option value="live">Current live (with manual edits)</option>
        </select>
        <button data-testid="compare-run" onClick={run} disabled={busy || !a || (!b && !bLive)}
          className="h-7 px-3 bg-[var(--c-accent)] text-white text-[11px] rounded-sm disabled:opacity-40">
          {busy ? "Comparing…" : "Compare"}
        </button>
      </div>

      {data && (
        <>
          <div className="text-[11px] font-mono text-[var(--c-muted)]" data-testid="compare-summary">
            v{data.a_version} → {data.b_version === "live" ? "live" : `v${data.b_version}`}:
            <span className="text-[var(--c-amber)]"> {data.summary.changed} changed</span>,
            <span className="text-[var(--c-green)]"> {data.summary.added} added</span>,
            <span className="text-[var(--c-red)]"> {data.summary.removed} removed</span>
            {" | "}Weight: {Number(data.summary.weight_a || 0).toLocaleString()} → {Number(data.summary.weight_b || 0).toLocaleString()} kg ({pct(data.summary.weight_a, data.summary.weight_b)})
            {" | "}Cost: {inr(data.summary.cost_a)} → {inr(data.summary.cost_b)} ({pct(data.summary.cost_a, data.summary.cost_b)})
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-[var(--c-surface)]">
                <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
                  <th className="py-2 px-2">Item</th><th className="py-2 px-2">Description</th>
                  <th className="py-2 px-2">v{data.a_version}</th>
                  <th className="py-2 px-2">{data.b_version === "live" ? "Live" : `v${data.b_version}`}</th>
                  <th className="py-2 px-2">Change</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.item_number} className="border-b border-[var(--c-border)]/40" style={{ background: STATUS_STYLE[r.status].bg }}
                    data-testid={`compare-row-${r.item_number}`}>
                    <td className="py-1 px-2 font-mono text-[10px] text-[var(--c-muted)]">{r.item_number}</td>
                    <td className="py-1 px-2 text-[11px] text-[var(--c-text)]">{r.description}</td>
                    <td className="py-1 px-2 font-mono text-[10px] text-[var(--c-muted)]">{fmtCell(r.a)}</td>
                    <td className="py-1 px-2 font-mono text-[10px] text-[var(--c-text)]">{fmtCell(r.b)}</td>
                    <td className="py-1 px-2 text-[10px]" style={{ color: STATUS_STYLE[r.status].color }}>
                      {STATUS_STYLE[r.status].label}
                      {r.changed_fields?.length > 0 && <span className="text-[var(--c-muted)]"> ({r.changed_fields.join(", ")})</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
